import { useCallback, useState } from 'react';
import errorLogger, {
  BUGSNAG_STATUSES,
} from '@picsart/growth-rc/services/errorLogger';

import {
  addRecentSearch,
  clearRecentSearches,
  loadRecentSearches,
  removeRecentSearch,
} from '@/utils/storage/recentSearchStorage';

const reportFailure = (error: unknown) =>
  errorLogger.trigger(error as Error, BUGSNAG_STATUSES.WARN);

/**
 * The user's past search terms, persisted in miniapp storage.
 *
 * Every mutation resolves to the next list from the storage layer, so state
 * and disk can't drift; a failed write leaves both untouched rather than
 * showing a term that won't survive a reload.
 */
const useRecentSearches = () => {
  const [recentSearches, setRecentSearches] =
    useState<string[]>(loadRecentSearches);

  const remember = useCallback((term: string) => {
    if (!term) return;
    addRecentSearch(term).then(setRecentSearches).catch(reportFailure);
  }, []);

  const forget = useCallback((term: string) => {
    removeRecentSearch(term).then(setRecentSearches).catch(reportFailure);
  }, []);

  const forgetAll = useCallback(() => {
    clearRecentSearches().then(setRecentSearches).catch(reportFailure);
  }, []);

  return { recentSearches, remember, forget, forgetAll };
};

export default useRecentSearches;
